import React, { FC, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { ReportModel } from 'src/models';
import { useAppData, useLoading } from '../../../providers';
import { Header, ReportsGeneralToolbar, PageTitle, QuickReportWithPercentage, ReportTitle } from '../../../components';
import { useStyles } from './styles';
import { PartnershipReports } from './components';
import useGetReports from './hooks/useGetReports';
import { useCommonStyles } from '../../../styles';
import { MasterIbCommissionModel } from './MasteIBCommissionModel';
import { ColumnConfig, formatCurrency } from '../DataTable';

export const MasterIbCommission: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { userData, translations } = useAppData();
  const { setIsLoading } = useLoading();
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const [reportData, setReportData] = useState<MasterIbCommissionModel>();
  const [showReports, setShowReports] = useState<boolean>(false);
  const [loginData, setLoginData] = useState<any>(null);
  const [translation, setTranslation] = useState<any>([]);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  useEffect(() => {
    const userLoginData = JSON.parse(userData);
    setLoginData(userLoginData);
  }, [userData]);

  const columns = useMemo(
    (): ColumnConfig<ReportModel>[] => [
      { key: 'partnershipCode', label: translation.text_6009 || '', type: 'string' },
      { key: 'partnershipType', label: translation.text_572 || '', type: 'string' },
      { key: 'schemeName', label: translation.text_579 || '', type: 'string' },
      { key: 'subPartnershipsRebateSum', label: translation.text_982 || '', type: 'number', format: (v) => formatCurrency(v), summable: true },
      { key: 'depositUSD', label: translation.text_983 || '', type: 'number', format: (v) => formatCurrency(v), summable: true },
      {
        key: 'withdrawalUSD',
        label: translation.text_6344 || '',
        type: 'number',
        format: (v) => {
          const num = Number(v);
          return Number.isNaN(num) ? '-' : `$${Math.abs(num).toFixed(2)}`;
        },
        summable: true,
      },
      { key: 'schemeType', label: translation.text_6010 || '', type: 'string' },
    ],
    [translation],
  );

  useEffect(() => {
    if (columns.length === 7) {
      setSelected(columns.slice(0, 6).map((col) => col.label));
    }
  }, [columns]);

  const handleNewReportRequest = useGetReports({
    dateRange: selectedDateRange,
    setReportData,
    showReports,
    setShowReports,
    setIsLoading,
  });

  return (
    <>
      <Header title={translation.text_6663} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            title={translation.text_6656}
            subTitle={translation.masterIBReportTooltip}
            exportFileName={translation.text_6656}
            columns={columns}
            allData={reportData?.items || []}
            selectedColumns={selected}
            printColumnNumber={selected.length}
            content={
              <PartnershipReports
                translation={translation}
                selectedDateRange={selectedDateRange}
                partnerName={loginData?.userDisplayName}
                columns={columns}
                selectedColumns={selected}
                rows={reportData?.items || []}
                onSelectColumns={setSelected}
              />
            }
            enableButtons={showReports}
            translation={translation}
            reportNameInEn="Master IB Commission"
          />
          <Grid container direction="column">
            <ReportsGeneralToolbar translation={translation} dateRange={selectedDateRange} handleDateRangeChange={(value) => setSelectedDateRange(value)} showButton handleButtonClick={handleNewReportRequest} />
            <Grid item xs={12}>
              <Grid container direction="row" spacing={2} className={classes.panels}>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_982} value={reportData?.total.subPartnershipsRebateSum || 0} percentage={reportData?.total.subPartnershipsRebateSumPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_983} value={reportData?.total.depositUSD || 0} percentage={reportData?.total.depositUSDPercentage || 0} />
                </Grid>
                <Grid item className={classes.quickReports}>
                  <QuickReportWithPercentage title={translation.text_6344} value={reportData?.total.withdrawalUSD || 0} percentage={reportData?.total.withdrawalUSDPercentage || 0} />
                </Grid>
              </Grid>
              <PartnershipReports
                translation={translation}
                selectedDateRange={selectedDateRange}
                partnerName={loginData?.userDisplayName}
                columns={columns}
                selectedColumns={selected}
                rows={reportData?.items || []}
                onSelectColumns={setSelected}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
